// =============================================
// Pillar Bulk Generation Modal (P-E faza P)
//
// Lista wszystkich seedow mapy + checkbox per seed + status (czy pillar istnieje, engine_stage).
// "Generuj zaznaczone (N)" -> SEKWENCYJNIE per seed:
//  1. Jesli brak pillar node -> utworz (INSERT thematic_nodes, idx_unique_pillar_per_seed dba o no-duplicate)
//  2. generatePillar(pillarNodeId, mapId) z onProgress
//  3. Per-seed wynik OK/error -> "Retry failed" gdy errors > 0
// Soft fail-fast: blad jednego nie przerywa reszty.
// Bez publikacji WP.
// =============================================

import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { generatePillar } from "@/services/contentEngine/pillarGenerator";
import { hashQuestion } from "@/services/deduplicationService";
import { uiLog } from "@/services/contentEngine/contentLogger";
import { toast } from "@/hooks/use-toast";
import type { ThematicMap, ThematicSeed, ThematicNode } from "@/types/thematicMaps";

export interface PillarBulkGenerationModalProps {
  open: boolean;
  map: ThematicMap;
  seeds: ThematicSeed[];
  nodes: ThematicNode[];
  onClose: () => void;
}

interface SeedRow {
  seed: ThematicSeed;
  existingPillar: ThematicNode | null;
  selected: boolean;
  status: "pending" | "running" | "ok" | "error";
  currentStage?: string;
  error?: string;
}

function slugify(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").trim()
    .replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 80);
}

export function PillarBulkGenerationModal({
  open, map, seeds, nodes, onClose,
}: PillarBulkGenerationModalProps) {
  const [rows, setRows] = useState<SeedRow[]>([]);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<{ ok: number; error: number } | null>(null);

  // Build rows on open (seeds + match existing pillar per seed)
  useEffect(() => {
    if (!open) {
      setRunning(false);
      setSummary(null);
      return;
    }
    const initial: SeedRow[] = seeds.map((seed) => {
      const existingPillar = nodes.find((n) => {
        const role = (n as { document_role?: string }).document_role;
        return n.seed_id === seed.id && role === "pillar";
      }) ?? null;
      return {
        seed,
        existingPillar,
        selected: true, // default all selected
        status: "pending",
      };
    });
    setRows(initial);
  }, [open, seeds, nodes]);

  const toggleSelect = (seedId: string) => {
    setRows((prev) => prev.map((r) => r.seed.id === seedId ? { ...r, selected: !r.selected } : r));
  };
  const selectAll = (val: boolean) => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: val })));
  };

  const selectedCount = rows.filter((r) => r.selected).length;

  const updateRow = (seedId: string, patch: Partial<SeedRow>) => {
    setRows((prev) => prev.map((r) => r.seed.id === seedId ? { ...r, ...patch } : r));
  };

  /**
   * Per seed:
   * 1. Jesli brak pillar -> INSERT thematic_nodes (idx_unique_pillar_per_seed dba o no-dup)
   * 2. generatePillar(pillarNodeId, mapId, onProgress)
   * Soft fail-fast: blad jednego nie przerywa reszty.
   */
  const runOneSeed = async (row: SeedRow): Promise<{ ok: boolean; error?: string }> => {
    updateRow(row.seed.id, { status: "running", currentStage: "init" });
    try {
      // 1. Resolve pillar node (utwórz jesli brak)
      let pillarNodeId = row.existingPillar?.id ?? null;
      if (!pillarNodeId) {
        updateRow(row.seed.id, { currentStage: "creating pillar node..." });
        const { data: { user } } = await (supabase as any).auth.getUser();
        if (!user) throw new Error("Nie zalogowany");

        const seedTopic = (row.seed.name || "ten temat").trim();
        const title = seedTopic;
        const slug = slugify(row.seed.slug || row.seed.name || "pillar");
        const canonicalQ = `Czym jest ${seedTopic} i od czego zacząć?`;

        const { data: newNode, error: insErr } = await (supabase as any)
          .from("thematic_nodes")
          .insert({
            user_id: user.id,
            map_id: map.id,
            seed_id: row.seed.id,
            title,
            slug,
            canonical_question: canonicalQ,
            canonical_question_hash: hashQuestion(canonicalQ),
            document_role: "pillar",
            node_role: "pillar",
            node_status: "planned",
            map_version: map.map_version,
            sort_order: 0,
          })
          .select("id")
          .single();

        if (insErr) throw new Error(`INSERT pillar node failed: ${insErr.message}`);
        pillarNodeId = newNode.id;
        uiLog.public("PillarBulk", `Created pillar node ${pillarNodeId} dla seed "${row.seed.name}"`);
      }

      // 2. Generate pillar content (sequential)
      if (!pillarNodeId) {
        throw new Error("pillarNodeId is null after insert");
      }
      const finalPillarId: string = pillarNodeId;
      const result = await generatePillar(finalPillarId, map.id, (_msg, stage) => {
        updateRow(row.seed.id, { currentStage: stage || "running" });
      });

      if (!result.success) {
        return { ok: false, error: result.error || "generation failed" };
      }
      return { ok: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false, error: msg };
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setSummary(null);
    const selectedRows = rows.filter((r) => r.selected);
    uiLog.public("PillarBulk", `START bulk: ${selectedRows.length} seedow, sekwencyjnie`);

    let ok = 0;
    let error = 0;
    for (const row of selectedRows) {
      const result = await runOneSeed(row);
      if (result.ok) {
        updateRow(row.seed.id, { status: "ok", currentStage: undefined });
        ok++;
      } else {
        updateRow(row.seed.id, { status: "error", error: result.error, currentStage: undefined });
        error++;
      }
    }

    setSummary({ ok, error });
    setRunning(false);
    uiLog.public("PillarBulk", `END bulk: ${ok} OK, ${error} blędow`);
    toast({
      title: `Bulk pillar: ${ok} OK, ${error} blędow`,
      description: error > 0 ? "Kliknij 'Retry failed' aby ponowic błędne." : "Wszystkie wygenerowane.",
      variant: error > 0 ? "destructive" : "default",
    });
  };

  const handleRetryFailed = () => {
    // Mark only error rows as selected, reset status to pending
    setRows((prev) => prev.map((r) => r.status === "error"
      ? { ...r, selected: true, status: "pending", error: undefined }
      : { ...r, selected: false }));
    setSummary(null);
  };

  const hasErrors = (summary?.error ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk generacja pillarów</DialogTitle>
          <DialogDescription>
            Lista wszystkich seedów mapy. Zaznacz seedy do wygenerowania pillara — jeden po drugim (sekwencyjnie). Bez publikacji WP.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between border-b pb-2">
          <div className="flex items-center gap-2 text-sm">
            <Button size="sm" variant="ghost" onClick={() => selectAll(true)} disabled={running}>
              Zaznacz wszystko
            </Button>
            <Button size="sm" variant="ghost" onClick={() => selectAll(false)} disabled={running}>
              Odznacz wszystko
            </Button>
            <span className="text-xs text-muted-foreground">
              {selectedCount} / {rows.length} zaznaczonych
            </span>
          </div>
          {hasErrors && !running && (
            <Button size="sm" variant="outline" onClick={handleRetryFailed}>
              Retry failed ({summary?.error})
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-1.5 -mr-2 pr-2">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Brak seedów w mapie. Najpierw wygeneruj seeds.
            </p>
          )}
          {rows.map((row) => {
            const stageBadge =
              row.status === "ok" ? "ready" :
              row.status === "error" ? "error" :
              row.status === "running" ? row.currentStage || "running" :
              row.existingPillar ? "(pillar istnieje — re-generate)" : "(pillar zostanie utworzony)";

            return (
              <div
                key={row.seed.id}
                className={`flex items-start gap-3 p-2.5 rounded border ${
                  row.status === "ok" ? "bg-green-500/5 border-green-500/30" :
                  row.status === "error" ? "bg-destructive/5 border-destructive/30" :
                  row.status === "running" ? "bg-blue-500/5 border-blue-500/30" :
                  "bg-muted/20"
                }`}
              >
                <Checkbox
                  checked={row.selected}
                  onCheckedChange={() => toggleSelect(row.seed.id)}
                  disabled={running}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate">{row.seed.name}</p>
                    {row.existingPillar && (
                      <Badge variant="outline" className="text-[10px]">
                        existing pillar
                      </Badge>
                    )}
                    {row.status === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />}
                    {row.status === "error" && <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />}
                    {row.status === "running" && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {stageBadge}
                  </p>
                  {row.status === "error" && row.error && (
                    <p className="text-xs text-destructive mt-0.5 font-mono break-all">
                      {row.error.slice(0, 200)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {summary && !running && (
          <div className={`rounded-md border p-3 text-sm ${
            summary.error > 0 ? "border-destructive/40 bg-destructive/10" : "border-green-500/30 bg-green-500/5"
          }`}>
            <p className="font-medium">
              Bulk zakonczony: <span className="text-green-700">{summary.ok} OK</span>
              {summary.error > 0 && (
                <> · <span className="text-destructive">{summary.error} blędow</span></>
              )}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={running}>
            {running ? "W toku..." : summary ? "Zamknij" : "Anuluj"}
          </Button>
          {!summary && (
            <Button
              onClick={handleRun}
              disabled={running || selectedCount === 0}
            >
              {running ? (
                <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Generowanie... </>
              ) : (
                <><Sparkles className="mr-2 h-3.5 w-3.5" /> Generuj zaznaczone ({selectedCount})</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
